import React, { useState, useMemo } from 'react';
import { structureAPI } from '../../services/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus, Download, Archive, MoreHorizontal, Filter, 
  User, Building2, ChevronUp, ChevronDown, Users 
} from 'lucide-react';
import RoleDetailDrawer from './RoleDetailDrawer';

const DirectoryTab = ({ roles, departments, people, loading, onUpdate, searchQuery }) => {
  const [selectedRoles, setSelectedRoles] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'title', direction: 'asc' });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  // Filter and sort roles
  const filteredRoles = useMemo(() => {
    let result = [...roles];
    
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(role => 
        role.title.toLowerCase().includes(query) ||
        role.assignedPerson?.displayName?.toLowerCase().includes(query) ||
        role.department?.name?.toLowerCase().includes(query)
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'vacant') {
        result = result.filter(role => !role.assignedPersonId);
      } else {
        result = result.filter(role => role.status === statusFilter);
      }
    }
    
    // Apply department filter
    if (deptFilter !== 'all') {
      result = result.filter(role => role.departmentId === deptFilter);
    }
    
    // Apply sort
    result.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'title':
          aVal = a.title.toLowerCase();
          bVal = b.title.toLowerCase();
          break;
        case 'department':
          aVal = a.department?.name?.toLowerCase() || '';
          bVal = b.department?.name?.toLowerCase() || '';
          break;
        case 'person':
          aVal = a.assignedPerson?.displayName?.toLowerCase() || 'zzz';
          bVal = b.assignedPerson?.displayName?.toLowerCase() || 'zzz';
          break;
        case 'level':
          aVal = a.levelLabel?.toLowerCase() || 'zzz';
          bVal = b.levelLabel?.toLowerCase() || 'zzz';
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [roles, searchQuery, statusFilter, deptFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRoles(new Set(filteredRoles.map(r => r.id)));
    } else {
      setSelectedRoles(new Set());
    }
  };

  const handleSelectRole = (roleId, checked) => {
    const newSelected = new Set(selectedRoles);
    if (checked) {
      newSelected.add(roleId);
    } else {
      newSelected.delete(roleId);
    }
    setSelectedRoles(newSelected);
  };

  const handleBulkArchive = async () => {
    try {
      const promises = Array.from(selectedRoles).map(id => 
        structureAPI.archiveRole(id)
      );
      await Promise.all(promises);
      toast.success(`${selectedRoles.size} role(s) archived`);
      setSelectedRoles(new Set());
      onUpdate();
    } catch (error) {
      console.error('Error archiving roles:', error);
      toast.error('Failed to archive some roles');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Title', 'Department', 'Level', 'Assigned Person', 'Email', 'Status'];
    const rows = filteredRoles.map(role => [
      role.title,
      role.department?.name || '',
      role.levelLabel || '',
      role.assignedPerson?.displayName || 'Vacant',
      role.assignedPerson?.email || '',
      role.status
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'organization-directory.csv';
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Directory exported successfully');
  };

  const handleRoleClick = (role) => {
    setSelectedRole(role);
    setDrawerOpen(true);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 inline ml-1" />
      : <ChevronDown className="w-4 h-4 inline ml-1" />;
  };

  if (loading) {
    return (
      <div 
        className="flex items-center justify-center h-64 rounded-xl"
        style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}
      >
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" 
          style={{ borderColor: '#2A9D8F', borderTopColor: 'transparent' }} 
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="directory-tab">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" style={{ color: '#6B7280' }} />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="vacant">Vacant</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Department Filter */}
          <Select value={deptFilter} onValueChange={setDeptFilter}>
            <SelectTrigger className="w-[160px]" data-testid="dept-filter">
              <Building2 className="w-4 h-4 mr-2" style={{ color: '#6B7280' }} />
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map(dept => (
                <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          {selectedRoles.size > 0 && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleBulkArchive}
              style={{ color: '#EF4444', borderColor: '#FCA5A5' }}
              data-testid="bulk-archive-btn"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archive ({selectedRoles.size})
            </Button>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleExportCSV}
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button 
            size="sm"
            onClick={() => {
              setSelectedRole(null);
              setDrawerOpen(true);
            }}
            style={{ backgroundColor: '#2A9D8F', color: '#FFFFFF' }}
            data-testid="add-role-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Role
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total Roles', value: roles.filter(r => r.status !== 'archived').length, icon: Users },
          { label: 'Filled', value: roles.filter(r => r.assignedPersonId && r.status !== 'archived').length, color: '#10B981' },
          { label: 'Vacant', value: roles.filter(r => !r.assignedPersonId && r.status !== 'archived').length, color: '#F59E0B' },
          { label: 'Archived', value: roles.filter(r => r.status === 'archived').length, color: '#6B7280' }
        ].map((stat, i) => (
          <div 
            key={i}
            className="p-4 rounded-lg"
            style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
          >
            <p className="text-sm" style={{ color: '#6B7280' }}>{stat.label}</p>
            <p className="text-2xl font-semibold" style={{ color: stat.color || '#264653' }}>
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div 
        className="rounded-lg overflow-hidden"
        style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
      >
        <Table>
          <TableHeader>
            <TableRow style={{ backgroundColor: '#F9FAFB' }}>
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={selectedRoles.size === filteredRoles.length && filteredRoles.length > 0}
                  onCheckedChange={handleSelectAll}
                  data-testid="select-all-checkbox"
                />
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('title')}
              >
                Role Title <SortIcon columnKey="title" />
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('department')}
              >
                Department <SortIcon columnKey="department" />
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('level')}
              >
                Level <SortIcon columnKey="level" />
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('person')}
              >
                Assigned To <SortIcon columnKey="person" />
              </TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRoles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8" style={{ color: '#6B7280' }}>
                  No roles found matching your filters
                </TableCell>
              </TableRow>
            ) : (
              filteredRoles.map(role => (
                <TableRow 
                  key={role.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleRoleClick(role)}
                  data-testid={`directory-row-${role.id}`}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedRoles.has(role.id)}
                      onCheckedChange={(checked) => handleSelectRole(role.id, checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium" style={{ color: '#1F2937' }}>{role.title}</div>
                    {role.reportsTo && (
                      <div className="text-xs" style={{ color: '#6B7280' }}>Reports to: {role.reportsTo}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" style={{ color: '#6B7280' }} />
                      <span style={{ color: '#374151' }}>{role.department?.name || '-'}</span>
                    </div>
                  </TableCell>
                  <TableCell style={{ color: '#374151' }}>
                    {role.levelLabel || '-'}
                  </TableCell>
                  <TableCell>
                    {role.assignedPerson ? (
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium"
                          style={{ backgroundColor: '#E8F4F2', color: '#2A9D8F' }}
                        >
                          {role.assignedPerson.displayName?.split(' ').map(n => n[0]).join('') || 'U'}
                        </div>
                        <span style={{ color: '#374151' }}>{role.assignedPerson.displayName}</span>
                      </div>
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {role.status === 'archived' ? (
                      <Badge variant="secondary" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>
                        Archived
                      </Badge>
                    ) : role.assignedPersonId ? (
                      <Badge style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>
                        Active
                      </Badge>
                    ) : (
                      <Badge style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                        Vacant
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleRoleClick(role)}>
                          Edit Role
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => structureAPI.archiveRole(role.id).then(onUpdate)}
                          className="text-red-600"
                        >
                          Archive
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Role Detail Drawer */}
      <RoleDetailDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedRole(null);
        }}
        role={selectedRole}
        departments={departments}
        people={people}
        onUpdate={onUpdate}
      />
    </div>
  );
};

export default DirectoryTab;
